import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PackageOpen, CheckCircle2, Clock, XCircle, Truck, RefreshCw, Search } from 'lucide-react';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

/**
 * Kunden-Retouren-Seite: Antrag stellen oder Status prüfen.
 * Öffentlich (Gast + eingeloggt).
 */
const STATUS_INFO = {
  pending:  { label: 'Eingegangen', icon: Clock, color: '#8B7355' },
  approved: { label: 'Genehmigt',   icon: CheckCircle2, color: '#2F6B4F' },
  received: { label: 'Ware erhalten', icon: Truck, color: '#5D4E3C' },
  refunded: { label: 'Rückerstattet', icon: CheckCircle2, color: '#2F6B4F' },
  rejected: { label: 'Abgelehnt',    icon: XCircle, color: '#B0512E' },
};

const ReturnsPage = () => {
  const [tab, setTab] = useState('request'); // 'request' | 'status'
  const [params] = useSearchParams();
  const [form, setForm] = useState({ order_number: '', email: '', reason: '', preferred_solution: 'Rückerstattung' });
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);

  const [lookupForm, setLookupForm] = useState({ order_number: '', email: '' });
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    const t = params.get('tab');
    if (t === 'status') setTab('status');
  }, [params]);

  const submitReturn = async (e) => {
    e.preventDefault();
    if (!form.order_number || !form.email || form.reason.trim().length < 5) {
      toast.error('Bitte alle Pflichtfelder ausfüllen (Grund mind. 5 Zeichen)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/returns', form);
      setTicket(res.data?.ticket);
      toast.success(`Retoure eingereicht — Ticket: ${res.data?.ticket}`);
      setForm({ order_number: '', email: '', reason: '', preferred_solution: 'Rückerstattung' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Antrag konnte nicht gesendet werden');
    } finally {
      setSubmitting(false);
    }
  };

  const lookupStatus = async (e) => {
    e.preventDefault();
    if (!lookupForm.order_number || !lookupForm.email) return;
    setLookupLoading(true);
    try {
      const res = await axiosInstance.get('/returns/lookup', {
        params: { order_number: lookupForm.order_number, email: lookupForm.email },
      });
      setLookupResult(res.data?.returns || []);
    } catch (err) {
      toast.error('Status konnte nicht abgerufen werden');
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF5EE] py-8 md:py-12" data-testid="returns-page">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <PackageOpen className="h-8 w-8 text-[#C4A084]" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
              Retouren
            </h1>
            <p className="text-sm text-[#8B7355]">Rückgabe innerhalb von 14 Tagen möglich — wir sind kulant, sag uns einfach was los ist.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E8DCC8] mb-6">
          <button
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              tab === 'request' ? 'border-[#5D4E3C] text-[#5D4E3C]' : 'border-transparent text-[#8B7355]'
            }`}
            onClick={() => setTab('request')}
            data-testid="returns-tab-request"
          >
            Retoure einreichen
          </button>
          <button
            className={`px-4 py-2 text-sm font-semibold border-b-2 ${
              tab === 'status' ? 'border-[#5D4E3C] text-[#5D4E3C]' : 'border-transparent text-[#8B7355]'
            }`}
            onClick={() => setTab('status')}
            data-testid="returns-tab-status"
          >
            Status prüfen
          </button>
        </div>

        {tab === 'request' && (
          <div className="bg-white border border-[#E8DCC8] p-5 md:p-6">
            {ticket ? (
              <div className="text-center py-8" data-testid="returns-success">
                <CheckCircle2 className="h-14 w-14 text-[#2F6B4F] mx-auto mb-3" />
                <h2 className="text-xl font-bold text-[#5D4E3C] mb-2">Retoure erfolgreich eingereicht!</h2>
                <p className="text-sm text-[#8B7355] mb-4">
                  Deine Ticket-Nummer:
                  <br />
                  <span className="inline-block mt-2 bg-[#F5EDE4] text-[#5D4E3C] font-mono text-lg px-4 py-2 border border-[#C4A084]">
                    {ticket}
                  </span>
                </p>
                <p className="text-sm text-[#6B5B4A] max-w-md mx-auto">
                  Wir haben dir eine Bestätigungs-E-Mail geschickt. Sobald wir deinen Antrag geprüft haben,
                  bekommst du eine weitere Nachricht mit den nächsten Schritten.
                </p>
                <button
                  type="button"
                  onClick={() => setTicket(null)}
                  className="mt-6 text-sm text-[#8B7355] hover:text-[#5D4E3C] underline"
                >
                  Weitere Retoure einreichen
                </button>
              </div>
            ) : (
              <form onSubmit={submitReturn} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs uppercase tracking-wider text-[#8B7355]">Bestellnummer</span>
                    <input
                      type="text"
                      value={form.order_number}
                      onChange={(e) => setForm({ ...form, order_number: e.target.value })}
                      className="w-full mt-1 border border-[#E8DCC8] px-3 py-2 focus:border-[#C4A084] outline-none"
                      placeholder="z. B. HYS-2026-0001"
                      required
                      data-testid="returns-order-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-wider text-[#8B7355]">E-Mail-Adresse</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full mt-1 border border-[#E8DCC8] px-3 py-2 focus:border-[#C4A084] outline-none"
                      required
                      data-testid="returns-email-input"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#8B7355]">Wunsch</span>
                  <select
                    value={form.preferred_solution}
                    onChange={(e) => setForm({ ...form, preferred_solution: e.target.value })}
                    className="w-full mt-1 border border-[#E8DCC8] px-3 py-2 bg-white focus:border-[#C4A084] outline-none"
                  >
                    <option>Rückerstattung</option>
                    <option>Umtausch</option>
                    <option>Reparatur / Klärung</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#8B7355]">Grund der Rückgabe *</span>
                  <textarea
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    rows={5}
                    className="w-full mt-1 border border-[#E8DCC8] px-3 py-2 focus:border-[#C4A084] outline-none"
                    placeholder={'Beschreibe kurz, warum du zurückschicken möchtest (z. B. „Ware entspricht nicht der Beschreibung", „Größe passt nicht"). Je genauer, desto schneller können wir helfen.'}
                    required
                    data-testid="returns-reason-input"
                  />
                </label>

                <div className="text-xs text-[#8B7355] bg-[#F5EDE4] border border-[#E8DCC8] p-3">
                  <strong>So läuft&apos;s ab:</strong>
                  <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                    <li>Du reichst hier deinen Antrag ein — Bestätigung per E-Mail.</li>
                    <li>Wir prüfen und melden uns mit den Retourenhinweisen.</li>
                    <li>Du sendest die Ware zurück (Adresse in der Bestellbestätigung).</li>
                    <li>Rückerstattung nach Eingang & Prüfung — meist 2-5 Werktage.</li>
                  </ol>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#5D4E3C] hover:bg-[#4A3E30] disabled:bg-[#C4A084] text-white px-4 py-3 uppercase tracking-wider text-sm"
                  data-testid="returns-submit-btn"
                >
                  {submitting ? 'Wird gesendet…' : 'Retoure einreichen'}
                </button>
              </form>
            )}
          </div>
        )}

        {tab === 'status' && (
          <div className="bg-white border border-[#E8DCC8] p-5 md:p-6">
            <form onSubmit={lookupStatus} className="grid md:grid-cols-[1fr,1fr,auto] gap-3 mb-4">
              <input
                type="text"
                value={lookupForm.order_number}
                onChange={(e) => setLookupForm({ ...lookupForm, order_number: e.target.value })}
                placeholder="Bestellnummer"
                className="border border-[#E8DCC8] px-3 py-2 focus:border-[#C4A084] outline-none"
                required
              />
              <input
                type="email"
                value={lookupForm.email}
                onChange={(e) => setLookupForm({ ...lookupForm, email: e.target.value })}
                placeholder="E-Mail"
                className="border border-[#E8DCC8] px-3 py-2 focus:border-[#C4A084] outline-none"
                required
              />
              <button
                type="submit"
                disabled={lookupLoading}
                className="bg-[#5D4E3C] hover:bg-[#4A3E30] disabled:bg-[#C4A084] text-white px-4 py-2 inline-flex items-center gap-1"
              >
                <Search className="h-4 w-4" /> Prüfen
              </button>
            </form>

            {lookupResult === null ? (
              <p className="text-sm text-[#8B7355] py-6 text-center">
                Bestellnummer + E-Mail eingeben und &bdquo;Prüfen&ldquo; klicken.
              </p>
            ) : lookupResult.length === 0 ? (
              <p className="text-sm text-[#8B7355] py-6 text-center" data-testid="returns-lookup-empty">
                Zu dieser Bestellung wurde noch keine Retoure eingereicht.
                <br />
                <Link to="/retouren" className="underline text-[#5D4E3C]">
                  Retoure jetzt einreichen →
                </Link>
              </p>
            ) : (
              <ul className="divide-y divide-[#F0E4D0]" data-testid="returns-lookup-results">
                {lookupResult.map((r) => {
                  const info = STATUS_INFO[r.status] || STATUS_INFO.pending;
                  const Icon = info.icon;
                  return (
                    <li key={r.id} className="py-3">
                      <div className="flex items-start gap-3">
                        <Icon className="h-5 w-5 mt-0.5" style={{ color: info.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono bg-[#F5EDE4] px-2 py-0.5 text-[#5D4E3C]">
                              {r.ticket}
                            </span>
                            <span className="text-sm font-semibold" style={{ color: info.color }}>
                              {info.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#8B7355] mt-1">
                            Eingegangen am {new Date(r.created_at).toLocaleDateString('de-DE')}
                            {r.updated_at && r.updated_at !== r.created_at &&
                              ` · Zuletzt aktualisiert ${new Date(r.updated_at).toLocaleDateString('de-DE')}`}
                          </p>
                          <p className="text-sm text-[#5D4E3C] mt-2 line-clamp-3">{r.reason}</p>
                          {r.tracking_number && (
                            <p className="text-xs text-[#8B7355] mt-1">Sendungsnr.: <strong>{r.tracking_number}</strong></p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnsPage;
